/**
 * Brings an element into sight by scrolling only the part of the screen that scrolls around it, never the page
 * itself. On an iPhone `scrollIntoView` also moved the whole page of the Tafo app up under the clock, and it stayed
 * there (Furkan, 18/10/2026: the bar of the Kassa half behind the clock). Where the page itself is what scrolls (a
 * till pc), that is scrolled, as before.
 */
export function bringIntoView(
  element: HTMLElement,
  {
    block = "nearest",
    inline,
    smooth = false,
  }: { block?: "start" | "nearest" | "end"; inline?: "nearest"; smooth?: boolean } = {},
): void {
  const still =
    typeof window.matchMedia !== "function" || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const behavior: ScrollBehavior = smooth && !still ? "smooth" : "auto";

  const down = scrollingParent(element, "y");
  const box = element.getBoundingClientRect();
  const room = down === null ? { top: 0, bottom: window.innerHeight } : down.getBoundingClientRect();
  const margin = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
  const scroller = down ?? document.scrollingElement ?? document.documentElement;
  let top: number | null = null;
  if (block === "start" || (block === "nearest" && box.top < room.top + margin)) {
    top = scroller.scrollTop + box.top - room.top - margin;
  } else if (box.bottom > room.bottom) {
    top = scroller.scrollTop + box.bottom - room.bottom;
  }
  if (top !== null && typeof scroller.scrollTo === "function") scroller.scrollTo({ top: Math.max(0, top), behavior });

  if (inline === "nearest") {
    const side = scrollingParent(element, "x");
    if (side !== null && typeof side.scrollTo === "function") {
      const area = side.getBoundingClientRect();
      if (box.left < area.left) side.scrollTo({ left: side.scrollLeft + box.left - area.left - 8, behavior });
      else if (box.right > area.right) side.scrollTo({ left: side.scrollLeft + box.right - area.right + 8, behavior });
    }
  }
}

/** The nearest parent that scrolls in this direction itself; null: the page. */
function scrollingParent(element: HTMLElement, axis: "x" | "y"): HTMLElement | null {
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    const overflow = axis === "y" ? style.overflowY : style.overflowX;
    const more = axis === "y" ? parent.scrollHeight > parent.clientHeight : parent.scrollWidth > parent.clientWidth;
    if ((overflow === "auto" || overflow === "scroll") && more) return parent;
  }
  return null;
}

/**
 * The till and the Tafo app on a phone: the page itself never scrolls, its parts do. Should iOS have moved it
 * anyway (a keyboard that closes, a jump to a field), it is put back as soon as no field has the focus.
 */
export function keepPageInPlace(): () => void {
  const back = () => {
    window.setTimeout(() => {
      const typing = document.activeElement?.matches("input, textarea, select, [contenteditable]") === true;
      if (!typing && (window.scrollY !== 0 || window.scrollX !== 0)) window.scrollTo(0, 0);
    }, 120);
  };
  document.addEventListener("focusout", back);
  window.addEventListener("scroll", back, { passive: true });
  window.visualViewport?.addEventListener("resize", back);
  return () => {
    document.removeEventListener("focusout", back);
    window.removeEventListener("scroll", back);
    window.visualViewport?.removeEventListener("resize", back);
  };
}
